import { ahuBoardApi } from './ahuBoardApi';

/**
 * Phase 7 — the `/partners/` data layer.
 *
 * Ports the two Backbone collections the page owns
 * (`django/partners/static/partners/mvc/collection/partners_company.js` and
 * `…/partners_contact.js`), fetched in parallel from `view/partners.js:59-61`,
 * plus the six writes of the partners discovery §3 endpoint table.
 *
 * Injected into `ahuBoardApi` (one reducerPath, no `store.ts` wiring) like the
 * 16 other `injectEndpoints` call sites; endpoint names carry a `Partners`
 * prefix, exactly as `sizerApi.ts` prefixes its own, so nothing can collide
 * with the selectors that already hit these two URLs.
 *
 * ★ WHY THE PREFIX MATTERS (discovery §11 risk 6)
 *
 * `/api/v1.0/projects/companies/` and `/api/v1.0/projects/contacts/` are ALSO
 * owned by `projectBoardApi.getBoardCompanies` / `getBoardContacts`
 * (`projectBoardApi.ts:155-162`) and by `dashboardApi`'s lookup pair
 * (`dashboardApi.ts:228-236`). Those live in their OWN `createApi` instances
 * (`reducerPath: 'projectBoardApi'` / `'dashboardApi'`), so:
 *
 *  - endpoint names and tag types are scoped per api instance — the only real
 *    collision risk is inside `ahuBoardApi`, which the `Partners` prefix and
 *    the `Partners*` tag types remove;
 *  - a Partners mutation therefore CANNOT invalidate `BoardCompanies` /
 *    `BoardContacts`, and that is the CORRECT behaviour, not a limitation.
 *    The legacy cross-app refresh contract is the four `localStorage` flags
 *    the Project Board reads on window focus (discovery §7 —
 *    `ProjectMainForm.tsx:136-158`); adding RTKQ cross-invalidation would be a
 *    behaviour change, and Partners always runs in a separate tab anyway
 *    (the header opens it with `target="blank"`).
 *
 * ★ NO ORDERING, NO PAGINATION, NO PARAMS. Both endpoints return the FULL list
 * ordered by `name` server-side (`project/models.py:54` and `:66`
 * `Meta.ordering = ('name',)`; neither viewset adds an `order_by`). The legacy
 * view renders `collection.toJSON()` in arrival order and never sorts, so
 * neither do we — no `transformResponse`, no client-side sort. The contacts
 * viewset does accept `?company_id=` (`viewset.py:891-893`) but this page never
 * uses it: it fetches every contact and groups them client-side
 * (`getCompanyContactCollection`, `partners.js:174-185`).
 */

// ---------------------------------------------------------------------------
// row shapes (verified against the live dev3 payloads captured in discovery §3)
// ---------------------------------------------------------------------------

/**
 * `GET /api/v1.0/projects/companies/`.
 *
 * Every field except `id`/`name` comes back `null` when unset — the serializer
 * declares them `CharField(required=False, allow_null=True)`
 * (`api/rest_project/serializers.py:293-322`). Modelled as `string | null` and
 * NOT as `string | undefined`: the UI coerces an emptied field to `null` on the
 * wire (never `""`, which would 400 — `allow_blank` defaults to False).
 */
export interface IPartnersCompany {
  id: number;
  /**
   * Owner id (`project_contact_company.user_id`), read-only on the wire.
   *
   * ★ THIS IS THE PAGE'S PERMISSION SIGNAL. `retrieve`, `update` and
   * `partial_update` all resolve the row as `get(pk=pk, user=request.user)`
   * (`api/rest_project/viewset.py:828`, `:847`, `:863`), so every write against
   * a company somebody else owns comes back **404 "Company doesn't exist"** —
   * while `list` (`:802-816`) hands a network user every company there is. The
   * rail therefore lists rows this user can only read, and until this field was
   * added the page had no way to tell them apart. Compare against
   * `getBootUserId()`; see `PartnersPage`'s `editableIds`.
   */
  user: number;
  name: string;
  address_line_one: string | null;
  address_line_two: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  tel_office: string | null;
  email: string | null;
  notes: string | null;
}

/**
 * `GET /api/v1.0/projects/contacts/` — ALL companies' contacts in one array.
 *
 * `name` is nullable: `addContact` creates a row with `{name: null, email:
 * null, tel_mobile: null}` (`partners.js:205`), so a freshly-added contact has
 * no name at all.
 */
export interface IPartnersContact {
  id: number;
  name: string | null;
  email: string | null;
  tel_mobile: string | null;
  company_id: number;
}

/**
 * POST body of `addContact` (`partners.js:205`) — `company_id` is the STRING
 * from `e.currentTarget.dataset.id`, and the server echoes back a NUMBER.
 *
 * ★ 7c widened this from `{company_id}` to the FOUR keys legacy actually puts
 * on the wire. `collection.create({company_id})` builds the model through
 * `_.defaults(_.extend({}, defaults, attrs), defaults)`, so the model's
 * `defaults` (`model/partners_contact.js:27-31`) land FIRST and
 * `Backbone.sync` serialises `model.toJSON()` — i.e.
 * `{"name":null,"email":null,"tel_mobile":null,"company_id":"103"}`, in that
 * key order. The three nulls are not optional decoration: they are what makes
 * the echo PATCH's changed-key set exactly `{id, company_id}` (Q1) instead of
 * also carrying the three fields.
 */
export interface IPartnersContactCreate {
  name: null;
  email: null;
  tel_mobile: null;
  company_id: string | number;
}

// ---------------------------------------------------------------------------

/**
 * `enhanceEndpoints` is how a NEW tag type reaches an existing `createApi`
 * instance — `injectEndpoints` alone cannot widen `tagTypes`. It returns the
 * same api object (same reducerPath, same cache), only re-typed.
 */
const partnersBaseApi = ahuBoardApi.enhanceEndpoints({
  addTagTypes: ['PartnersCompanies', 'PartnersContacts'] as const,
});

export const partnersApi = partnersBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    // =====================================================================
    // READS — the only two requests the page itself owns (discovery §3).
    // Both are fired in PARALLEL on load; see `PartnersPage.tsx`.
    // =====================================================================

    getPartnersCompanies: builder.query<IPartnersCompany[], void>({
      query: () => '/api/v1.0/projects/companies/',
      providesTags: ['PartnersCompanies'],
    }),

    getPartnersContacts: builder.query<IPartnersContact[], void>({
      query: () => '/api/v1.0/projects/contacts/',
      providesTags: ['PartnersContacts'],
    }),

    // =====================================================================
    // WRITES — DECLARED ONLY (phase 7a is read-only; 7c wires them).
    //
    // ⚠ DELIBERATELY NO `invalidatesTags` ANYWHERE BELOW. Legacy's refetch
    // pattern is hand-rolled and asymmetric (discovery §4), and blanket
    // invalidation would be a behaviour change on the wire:
    //   - field PATCH (company or contact) → NO refetch of anything;
    //   - create company → NO refetch; the collection is reordered in memory
    //     (new model pinned FIRST) and re-rendered;
    //   - create contact → NO refetch and NO re-render at all;
    //   - delete contact → `GET /contacts/` only;
    //   - delete company → `GET /contacts/` only (companies are NEVER
    //     re-fetched — the rail is rebuilt from the in-memory collection).
    // 7c should express those with explicit `util.updateQueryData` /
    // targeted `dispatch(…initiate(…, {forceRefetch:true}))`, not tags.
    //
    // ⚠ Q1 (discovery §5): every create is followed by a redundant PATCH of
    // the server's own echo — Backbone's `model.set(response)` fires `change`,
    // which the model binds to `save(this.changed, {patch:true})`. It is
    // observable on the wire and the verifier asserts it, so 7c must issue it
    // explicitly: company → `{id, address_line_one…notes}` (the 9 nulls),
    // contact → `{id, company_id}`.
    //
    // ⚠ §4.7: an over-length value returns a 500 HTML error page which the UI
    // swallows in silence. Mirror the swallow — no error state, no revert.
    // =====================================================================

    /** §4.1 — `POST /projects/companies/` `{name: "Company Name"}`. */
    createPartnersCompany: builder.mutation<IPartnersCompany, { name: string }>({
      query: (body) => ({
        url: '/api/v1.0/projects/companies/',
        method: 'POST',
        body,
      }),
    }),

    /** §4.2/§4.3 — `PATCH /projects/companies/<id>`, NO trailing slash
     * (Backbone `urlRoot` + `model.id`). Body is the changed keys ONLY. */
    patchPartnersCompany: builder.mutation<
      IPartnersCompany,
      { id: number; body: Partial<IPartnersCompany> }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/projects/companies/${id}`,
        method: 'PATCH',
        body,
      }),
    }),

    /** §4.9 — `DELETE /projects/companies/<id>`; contacts cascade server-side
     * (`ProjectCompanyContactPerson.company` is `on_delete=CASCADE`). */
    deletePartnersCompany: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v1.0/projects/companies/${id}`,
        method: 'DELETE',
      }),
    }),

    /** §4.5 — `POST /projects/contacts/`. Legacy sends
     * `{name:null, email:null, tel_mobile:null, company_id:"<id>"}` — the
     * three nulls come from the Backbone model defaults and `company_id` is a
     * STRING (`dataset.id`). Keep both quirks in 7c. */
    createPartnersContact: builder.mutation<IPartnersContact, IPartnersContactCreate>({
      query: (body) => ({
        url: '/api/v1.0/projects/contacts/',
        method: 'POST',
        body,
      }),
    }),

    /** §4.6 — `PATCH /projects/contacts/<id>`, NO trailing slash. */
    patchPartnersContact: builder.mutation<
      IPartnersContact,
      { id: number; body: Partial<IPartnersContact> }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/projects/contacts/${id}`,
        method: 'PATCH',
        body,
      }),
    }),

    /** §4.8 — `DELETE /projects/contacts/<id>`. */
    deletePartnersContact: builder.mutation<void, number>({
      query: (id) => ({
        url: `/api/v1.0/projects/contacts/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetPartnersCompaniesQuery,
  useGetPartnersContactsQuery,
  // 7c call sites — exported now so the mutations are reachable without
  // touching this file again.
  useCreatePartnersCompanyMutation,
  usePatchPartnersCompanyMutation,
  useDeletePartnersCompanyMutation,
  useCreatePartnersContactMutation,
  usePatchPartnersContactMutation,
  useDeletePartnersContactMutation,
} = partnersApi;
